import React from 'react';

import { Switch, Spin } from 'antd';
import {
  CheckCircleOutlined,
  KeyOutlined,
  MailOutlined,
  ReloadOutlined,
  StarOutlined,
  UnlockOutlined,
} from '@ant-design/icons';
import { useSelector } from 'dva';

import Badge from '@/components/Badge';
import TableRowButtons from '@/components/Common/TableRowButtons';
import tableRowButtonsStyles from '@/components/Common/TableRowButtons/style.less';
import UserAvatar from '@/components/Common/UserAvatar';
import UserGroupAddOutlined from '@/components/Common/UsergroupAddOutlined';
import HighlighterCustom from '@/components/HighlighterCustom';
import { DEFAULT_EMPTY_VALUE } from '@/utils/constants';
import Filters, { FILTER_FIELD_SIZES, registerModuleFilters } from '@/utils/input/filters';
import { buildAuditFilters } from '@/utils/input/filters/auditFilters';
import { CoreEnums } from '@/utils/domain/constants';
import { createEnumOptions, getEnumTranslationKey } from '@/utils/core/data-transformation';
import { createTableActionsColumn, getEnumNamespace, isItemEnabled } from '@/utils/infrastructure';

import { MODEL_NAME } from './constants';
import { renderJsonSelectValue } from './helpers';
import { getUserRoleFilterOptions, renderUserRolesBadges } from './roleHelpers';

const { BaseFilters } = Filters;

const RoleFilter = {
  remote: false,
  lazy: false,
  type: 'select',
  name: 'role',
  label: 'users.form.roles',
  locale: true,
  labelInValue: false,
  allowClear: true,
  needsQuotes: true,
  multiple: true,
  filterSize: FILTER_FIELD_SIZES.MEDIUM,
  options: getUserRoleFilterOptions(),
};

const TwoFactorEnabledFilter = {
  ...BaseFilters.BooleanFilter,
  name: 'userTwoFactorEnabled',
  oDataField: 'user/twoFactorEnabled',
  label: 'users.form.twoFactorEnabled',
  badgeOptions: {
    true: { status: 'success' },
    false: { status: 'default' },
  },
  options: [
    {
      label: 'global.text.enabled',
      value: 'true',
    },
    {
      label: 'global.text.disabled',
      value: 'false',
    },
  ],
  valueTransform: (value) => value === 'true',
};

const AccessTypeFilter = {
  ...BaseFilters.EnumFilter,
  name: 'accessType',
  label: 'users.form.accessType',
  enumNamespace: getEnumNamespace(CoreEnums.UserAccessType),
  enumSource: CoreEnums.UserAccessType,
  multiple: true,
  filterSize: FILTER_FIELD_SIZES.MEDIUM,
  options: createEnumOptions(CoreEnums.UserAccessType, 'users'),
};

const PositionFilter = {
  ...BaseFilters.RemoteFilter,
  name: 'positionId',
  label: 'users.form.positionId',
  knownType: CoreEnums.FormFieldType.InternalEntity,
  apiRoute: 'position/paged',
  filterSearch: true,
  multiple: true,
  filterSize: FILTER_FIELD_SIZES.MEDIUM,
  params: {
    select: 'id,name',
    filter: 'isActive eq true',
    orderBy: 'name asc',
  },
};

const AreaFilter = {
  ...BaseFilters.RemoteFilter,
  name: 'areaId',
  label: 'global.text.area',
  knownType: CoreEnums.FormFieldType.InternalEntity,
  apiRoute: 'area/paged',
  filterSearch: true,
  multiple: true,
  filterSize: FILTER_FIELD_SIZES.MEDIUM,
  params: {
    select: 'id,name',
    filter: 'isActive eq true',
    orderBy: 'name asc',
  },
};

const BossFilter = {
  ...Filters.UserFilter,
  name: 'userBossId',
  label: 'users.form.userBossId',
  labelInValue: true,
  filterSearch: true,
  multiple: true,
  filterSize: FILTER_FIELD_SIZES.MEDIUM,
};

export const getUserTwoFactorEnabled = (item) => {
  const value = item?.user?.twoFactorEnabled ?? item?.twoFactorEnabled;
  return value === true || value === 1;
};

export const isUserTwoFactorDisabled = (item) => !getUserTwoFactorEnabled(item);

export const isUserTwoFactorEnabled = (item) => getUserTwoFactorEnabled(item);

const UserTwoFactorSwitch = ({ record, onSetTwoFactor }) => {
  const loading = useSelector(
    (state) => !!state[MODEL_NAME]?.twoFactorUpdatingItemIds?.[record?.id],
  );

  return (
    <Switch
      checked={getUserTwoFactorEnabled(record)}
      loading={loading}
      onChange={(active) => onSetTwoFactor?.(record?.id, active)}
    />
  );
};

const [isActiveFilter, ...auditFilters] = buildAuditFilters();

const enabledByDefaultFilter = {
  ...isActiveFilter,
  defaultValue: 'true',
  applyDefault: true,
};

/** Applied on each visit so the list opens on enabled users. */
export const usersDefaultFilters = {
  isActive: 'true',
};

export const filtersConfig = [
  RoleFilter,
  PositionFilter,
  AreaFilter,
  BossFilter,
  TwoFactorEnabledFilter,
  AccessTypeFilter,
  enabledByDefaultFilter,
  ...auditFilters,
];

registerModuleFilters(MODEL_NAME, filtersConfig);

/**
 * Catalog for the stable `buildUsersColumns` on the users descriptor.
 * The page publishes it on render; the list effect reads it when `list` changes.
 */
let userListExtraFields = [];

export function syncUserListExtraFields(extraFields) {
  userListExtraFields = Array.isArray(extraFields) ? extraFields : [];
}

const buildExtraFieldColumns = (extraFields, searchCriteria) =>
  (Array.isArray(extraFields) ? extraFields : [])
    .filter((field) => field?.isManagementVisible)
    .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0))
    .map((field) => ({
      key: field.internalName,
      title: field.displayText,
      dataIndex: field.internalName,
      render: (_value, item) => {
        const value = item.extraData?.[field.internalName];

        if (typeof value === 'object' && value?.attachments) {
          return value.attachments.map((attachment) => (
            <img
              style={{ width: 50, height: 50 }}
              key={attachment.fileName}
              src={
                attachment.isBase64
                  ? `data:${attachment.contentType};base64,${attachment.content}`
                  : attachment.content
              }
              alt={attachment.fileName}
            />
          ));
        }

        const displayValue = renderJsonSelectValue(value, field);
        return (
          <div style={{ minWidth: '50px', width: 'max-content', maxWidth: '200px' }}>
            <HighlighterCustom textToHighlight={displayValue} searchTerm={searchCriteria} />
          </div>
        );
      },
    }));

/** Row action menu items available only for enabled users. */
const buildUserExtraMenuItems = (record, formatMessage, handlers) => {
  if (!isItemEnabled(record)) return [{ key: 'div-after-edit', type: 'divider' }];

  const accountActions = [
    {
      key: 'sendAccountConfirmationEmail',
      label: formatMessage({ id: 'global.button.sendAccountConfirmationEmail' }),
      icon: <MailOutlined />,
      onClick: () => handlers.sendAccountConfirmationEmail?.(record.id),
    },
    {
      key: 'confirmAccount',
      label: formatMessage({ id: 'global.button.confirmAccount' }),
      icon: <CheckCircleOutlined />,
      onClick: () => handlers.confirmAccount?.(record.id),
    },
    {
      key: 'sendPasswordRecoveryEmail',
      label: formatMessage({ id: 'global.button.sendPasswordRecoveryEmail' }),
      icon: <KeyOutlined />,
      onClick: () => handlers.sendRecoveryPasswordEmail?.(record.id),
    },
    {
      key: 'resetPassword',
      label: formatMessage({ id: 'global.button.resetPassword' }),
      icon: <ReloadOutlined />,
      onClick: () => handlers.toggleResetPassword?.(record.id),
    },
    {
      key: 'unlockAccount',
      label: formatMessage({ id: 'global.button.unlockAccount' }),
      icon: <UnlockOutlined />,
      onClick: () => handlers.unlockAccount?.(record.id),
    },
  ];

  if (record.isDefault !== true) {
    accountActions.push({
      key: 'setDefaultUser',
      label: formatMessage({ id: 'global.button.setDefaultUser' }),
      icon: <StarOutlined />,
      onClick: () => handlers.setDefaultUser?.(record.id, record.userId),
    });
  }

  return [
    { key: 'div-after-edit', type: 'divider' },
    {
      key: 'userGroup',
      label: formatMessage({ id: 'global.tooltip.userGroup' }),
      icon: (
        <div style={{ marginRight: '8px' }}>
          <UserGroupAddOutlined />
        </div>
      ),
      onClick: () => handlers.openUserGroups?.(record),
    },
    {
      key: 'userEnvironment',
      label: formatMessage({ id: 'global.tooltip.userEnviroment' }),
      icon: (
        <div style={{ marginRight: '8px' }}>
          <UserGroupAddOutlined />
        </div>
      ),
      onClick: () => handlers.openUserEnvironments?.(record),
    },
    { key: 'div-mid', type: 'divider' },
    ...accountActions,
    { key: 'div-before-delete', type: 'divider' },
  ];
};

const getUserRowActionOrder = (record) => ({
  primary: [],
  secondary: [
    'detail',
    'edit',
    'div-after-edit',
    ...(isItemEnabled(record)
      ? [
          'userGroup',
          'userEnvironment',
          'div-mid',
          'sendAccountConfirmationEmail',
          'confirmAccount',
          'sendPasswordRecoveryEmail',
          'resetPassword',
          'unlockAccount',
          ...(record.isDefault !== true ? ['setDefaultUser'] : []),
          'div-before-delete',
        ]
      : []),
    'delete',
  ],
});

const UsersRowActions = ({
  record,
  formatMessage,
  openDrawerCreateOrEdit,
  deleteItemConfirm,
  handlers,
}) => {
  const isRowActionLoading = useSelector(
    (state) => !!state[MODEL_NAME]?.rowActionLoadingItemIds?.[record.id],
  );

  if (isRowActionLoading) {
    return (
      <div className={tableRowButtonsStyles.loading}>
        <Spin size="small" />
      </div>
    );
  }

  return (
    <TableRowButtons
      onDetail={() => openDrawerCreateOrEdit(record.id, true)}
      onEdit={() => openDrawerCreateOrEdit(record.id, false)}
      onRemove={() => deleteItemConfirm(record.id)}
      customItems={buildUserExtraMenuItems(record, formatMessage, handlers)}
      formatMessage={formatMessage}
      forceDropdown
      actionOrder={getUserRowActionOrder(record)}
    />
  );
};

/**
 * Builds the Users list table columns (custom columns + dynamic extra-field columns).
 * Consumed by ConfigListPage via `useConfigListController`.
 */
export function buildUsersColumns({
  formatMessage,
  searchCriteria,
  openDrawerCreateOrEdit,
  deleteItemConfirm,
  activate,
  activatingItemIds = {},
  setTwoFactor,
  openUserGroups,
  openUserEnvironments,
  sendAccountConfirmationEmail,
  confirmAccount,
  sendRecoveryPasswordEmail,
  unlockAccount,
  setDefaultUser,
  toggleResetPassword,
  extraFields,
}) {
  const handlers = {
    openUserGroups,
    openUserEnvironments,
    sendAccountConfirmationEmail,
    confirmAccount,
    sendRecoveryPasswordEmail,
    unlockAccount,
    setDefaultUser,
    toggleResetPassword,
  };

  const baseColumns = [
    {
      key: 'isActive',
      title: formatMessage({ id: 'global.text.enabled' }),
      dataIndex: 'isActive',
      align: 'center',
      sorter: true,
      render: (isActive, item) => (
        <Switch
          checked={isActive}
          loading={!!activatingItemIds[item?.id]}
          onChange={(active) => activate(item?.id, active)}
        />
      ),
    },
    {
      key: 'user/twoFactorEnabled',
      title: formatMessage({ id: 'users.column.twoFactorEnabled' }),
      dataIndex: 'twoFactorEnabled',
      align: 'center',
      sorter: true,
      render: (_, item) => <UserTwoFactorSwitch record={item} onSetTwoFactor={setTwoFactor} />,
    },
    {
      key: 'fullName',
      title: formatMessage({ id: 'global.name' }),
      dataIndex: 'fullName',
      sorter: true,
      render: (text, item) => (
        <div style={{ width: '200px' }}>
          <UserAvatar
            {...item}
            renderName={<HighlighterCustom textToHighlight={text} searchTerm={searchCriteria} />}
          />
        </div>
      ),
    },
    {
      key: 'email',
      title: formatMessage({ id: 'global.email' }),
      dataIndex: 'email',
      sorter: true,
      render: (text) => <HighlighterCustom textToHighlight={text} searchTerm={searchCriteria} />,
    },
    {
      key: 'role',
      title: formatMessage({ id: 'users.form.roles' }),
      dataIndex: 'roles',
      sorter: true,
      render: (_el, item) => renderUserRolesBadges(item, formatMessage, searchCriteria),
    },
    {
      key: 'accessType',
      title: formatMessage({ id: 'users.form.accessType' }),
      dataIndex: 'accessType',
      sorter: true,
      render: (value) => {
        if (value === undefined || value === null) return DEFAULT_EMPTY_VALUE;
        const translationKey = getEnumTranslationKey(CoreEnums.UserAccessType, value, 'users');
        return (
          <Badge status="default" type="process" text={formatMessage({ id: translationKey })} />
        );
      },
    },
    {
      key: 'area/name',
      title: formatMessage({ id: 'global.text.area' }),
      dataIndex: 'area',
      sorter: true,
      render: (el) => (
        <div style={{ width: '150px' }}>
          <HighlighterCustom textToHighlight={el?.name} searchTerm={searchCriteria} />
        </div>
      ),
    },
    {
      key: 'position/name',
      title: formatMessage({ id: 'global.text.position' }),
      dataIndex: 'position',
      sorter: true,
      render: (el) => (
        <div style={{ width: '150px' }}>
          <HighlighterCustom textToHighlight={el?.name} searchTerm={searchCriteria} />
        </div>
      ),
    },
  ];

  const extraFieldColumns = buildExtraFieldColumns(
    extraFields === undefined ? userListExtraFields : extraFields,
    searchCriteria,
  );

  const actionsColumn = createTableActionsColumn({
    alwaysDropdown: true,
    render: (_, record) => (
      <UsersRowActions
        record={record}
        formatMessage={formatMessage}
        openDrawerCreateOrEdit={openDrawerCreateOrEdit}
        deleteItemConfirm={deleteItemConfirm}
        handlers={handlers}
      />
    ),
  });

  return [...baseColumns, ...extraFieldColumns, actionsColumn];
}
